import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, BadgeDollarSign, CalendarClock, RefreshCw } from "lucide-react";
import { Reveal, SectionTag, MaskedLine } from "@/components/Reveal";

const PACKAGES = [
  {
    id: "college-recruit",
    name: "College Recruit",
    tag: "Premium",
    featured: true,
    price: "$399",
    unit: "/ month",
    value: "Over $640 à la carte value",
    who: "Serious varsity & college prospects chasing a roster spot",
    includes: [
      "8 private 1-on-1 sessions per month ($480 value)",
      "Workout & training plan tailored to your player, rebuilt monthly",
      "2 film breakdown sessions with a written parent report",
      "A personalized recruiting roadmap — target-school list, coach outreach templates, and a film timeline built around your grad year",
      "Priority scheduling & direct line to Theo",
    ],
  },
  {
    id: "starting-5",
    name: "Starting 5 Varsity",
    tag: "Season Ready",
    price: "$299",
    unit: "/ month",
    value: "Over $440 à la carte value",
    who: "Varsity players who must show up to day one in shape",
    includes: [
      "4 private 1-on-1 sessions per month",
      "Unlimited group drop-in sessions",
      "Weight training & conditioning program for the upcoming season",
      "Bi-weekly film breakdown",
      "Pre-season agility testing & measurable benchmarks",
    ],
  },
  {
    id: "jv-jumpstart",
    name: "JV Jumpstart",
    tag: "Best First Step",
    price: "$149",
    unit: "/ month",
    value: "$180+ à la carte value",
    who: "JV & middle school athletes building real fundamentals",
    includes: [
      "1 private 1-on-1 session per month",
      "4 group drop-in sessions per month",
      "Personalized at-home drill plan",
      "Parent progress updates",
      "Full member blueprint vault access",
    ],
  },
  {
    id: "rookie",
    name: "Rookie Foundations",
    tag: "Entry",
    price: "$99",
    unit: "/ month",
    value: "$120 à la carte value",
    who: "Brand-new players, ages 11–13, learning to love the work",
    includes: [
      "4 group drop-in sessions per month",
      "Beginner footwork & shooting form curriculum",
      "Full member blueprint vault access",
      "Parent progress updates",
    ],
  },
  {
    id: "ten-pack",
    name: "Drop-in 10-Pack",
    tag: "Flexible",
    price: "$250",
    unit: "one-time",
    value: "$300 value — save $50",
    who: "Busy families who want training without a subscription",
    includes: [
      "10 group drop-in sessions, book any open slot",
      "Use anytime within 6 months",
      "Shareable between siblings",
      "Priority slot booking over single drop-ins",
    ],
  },
];

export default function Packages() {
  return (
    <main className="pt-16">
      <section className="relative court-grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-court-amber/10 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="inline-flex items-center gap-2 border border-court-amber/40 bg-court-amber/10 rounded-full px-4 py-1.5 mb-8">
            <BadgeDollarSign size={13} className="text-court-amber" />
            <span className="font-mono text-xs uppercase tracking-widest text-court-amber font-semibold">New — Monthly Training Packages</span>
          </div>
          <h1 data-testid="packages-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Train more.</MaskedLine>
            <MaskedLine delay={0.25}><span className="text-court-amber">Pay less per rep.</span></MaskedLine>
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Every package shows the à la carte price right next to the package price — so you always know exactly what you're saving. Month-to-month, pay in person, pause anytime.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <SectionTag index="SEC.01" label="The Lineup" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
            Five ways to <span className="text-court-amber">level up.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <div data-testid={`package-card-${p.id}`}
                className={`card-lift relative flex flex-col h-full bg-court-surface/90 border rounded-2xl p-7 ${p.featured ? "border-court-amber/50 shadow-[0_0_60px_rgba(255,107,0,0.12)]" : "border-white/10"}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-6 bg-court-amber text-court-void font-mono text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">Premium Tier</span>
                )}
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{p.tag}</div>
                <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mt-2">{p.name}</h3>
                <p className="text-sm text-zinc-400 mt-1.5">{p.who}</p>
                <div className="flex items-baseline gap-2 mt-5">
                  <span className={`font-display font-black text-5xl ${p.featured ? "text-court-amber" : ""}`}>{p.price}</span>
                  <span className="text-sm text-zinc-500">{p.unit}</span>
                </div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-court-amber mt-1.5">{p.value}</div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.includes.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-zinc-300"><CheckCircle2 size={15} className="text-court-amber shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link data-testid={`package-cta-${p.id}`} to="/schedule"
                  className={`mt-7 block text-center font-display font-extrabold uppercase tracking-wide text-lg py-3 rounded-lg ${p.featured ? "btn-amber bg-court-amber text-court-void" : "border border-white/20 hover:border-court-amber/60 text-zinc-100 transition-colors"}`}>
                  Ask About This Package
                </Link>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="flex flex-col justify-center h-full bg-court-amber/10 border border-court-amber/30 rounded-2xl p-7">
              <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">The Lab <span className="text-court-amber">promise</span></h3>
              <ul className="mt-5 space-y-4">
                <li className="flex gap-3 text-sm text-zinc-300"><RefreshCw size={16} className="text-court-amber shrink-0 mt-0.5" />Month-to-month. Pause or cancel anytime — no contracts, no guilt trips.</li>
                <li className="flex gap-3 text-sm text-zinc-300"><BadgeDollarSign size={16} className="text-court-amber shrink-0 mt-0.5" />Pay in person (cash, Zelle, or Venmo). No online payment required.</li>
                <li className="flex gap-3 text-sm text-zinc-300"><CalendarClock size={16} className="text-court-amber shrink-0 mt-0.5" />Unused private sessions roll over one month — you never lose what you paid for.</li>
              </ul>
              <Link data-testid="packages-schedule-cta" to="/schedule" className="btn-amber mt-7 inline-flex items-center justify-center gap-2 bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg px-6 py-3 rounded-lg">
                Check Availability <ArrowRight size={17} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
